import type { LessonStandard } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type LessonEditorService from "cc-frontend/services/lesson-editor"
import type StandardsModalService from "cc-frontend/services/standards-modal"
import { useCallback } from "react"
import { useEmberService } from "react-app/providers/ember-service-context"

/**
 * Returns a callback that opens the standards search modal, seeded with the lesson's current
 * standards, and resolves with the standards the user chose (the AddButton diffs these against the
 * current standards itself). Records the chosen standard sets on the planbook and course on the way
 * out.
 *
 * Lives at the caller level (lesson/unit) since that's where the course + planbook are available.
 */
export function useOpenStandardsModal(planbook: Planbook, course: Course) {
  const standardsModal = useEmberService<StandardsModalService>("standards-modal")
  const lessonEditor = useEmberService<LessonEditorService>("lesson-editor")

  return useCallback(
    async (startingStandards: LessonStandard[]): Promise<Array<LessonStandard>> => {
      const { standards, standardSetIds } = await standardsModal.openStandardsModal(
        startingStandards,
        planbook,
        course,
      )

      lessonEditor.updateStandardSetIds(
        standardSetIds,
        planbook.id,
        course.id,
        course.relationships.standardSets?.data.map(({ id }) => id) ?? [],
      )

      return standards.map((standard) => ({
        id: standard.id,
        code: standard.code,
        statement: standard.statement,
        jurisdiction: standard.jurisdiction,
        grades: standard.grades,
        standardSetId: standard.standardSetId,
        subject: standard.subject,
      }))
    },
    [standardsModal, lessonEditor, planbook, course],
  )
}
